if (typeof QuickBuy !== 'function') {
	class QuickBuy extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				button: '.button',
				iconButton: '.icon-button',
			};

			this.classes = {
				buttonLoading: 'button--loading',
				iconButton: 'icon-button',
				iconButtonLoading: 'icon-button--loading',
				hasError: 'has-error',
			};

			// Elements
			this.cartDrawer = document.querySelector('drawer-component[data-type="cart"]');
			this.drawer = document.querySelector('drawer-component[data-type="drawer"]');

			this.init();
		}

		initFlags() {
			this.variantId = this.dataset.id;
			this.productUrl = this.dataset.url;
			this.hasVariants = JSON.parse(this.dataset.hasVariants);
		}

		init() {
			const trigger = this;

			this.addEventListener('click', (e) => {
				e.preventDefault();

				this.addToCart(trigger);
			});
		}

		addToCart(trigger) {
			const { cartDrawer, drawer, classes, selectors } = this;
			const button =
				trigger.querySelector(selectors.iconButton) || trigger.querySelector(selectors.button) || trigger;
			const classToAdd = button.classList.contains(classes.iconButton)
				? classes.iconButtonLoading
				: classes.buttonLoading;

			this.initFlags();

			button.classList.add(classToAdd);
			button.setAttribute('disabled', true);

			if (this.hasVariants) {
				fetch(
					this.productUrl.includes('?')
						? `${this.productUrl}&variant=${this.variantId}&view=quickshop`
						: `${this.productUrl}?variant=${this.variantId}&view=quickshop`,
				)
					.then((response) => response.text())
					.then((responseText) => {
						const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

						this.productElement = responseHTML.querySelector('section[id^="ProductQuickshop-"]');
						this.preventVariantURLSwitching();

						drawer.replaceDrawerHeading(window.variantStrings.choose_options);
						drawer.replaceDrawerContent(this.productElement.innerHTML);

						if (window.Shopify && Shopify.PaymentButton) {
							Shopify.PaymentButton.init();
						}
					})
					.finally(() => {
						const imagesReloadEvent = new CustomEvent('lazy-image:reload', {
							bubbles: true,
							detail: this,
						});

						const cartDrawerLoadEvent = new CustomEvent('cart-drawer:load', {
							bubbles: true,
							detail: this,
						});

						document.dispatchEvent(imagesReloadEvent);
						document.dispatchEvent(cartDrawerLoadEvent);

						button.classList.remove(classToAdd);
						button.removeAttribute('disabled');

						drawer.openDrawer('drawer');

						setTimeout(() => {
							trapFocus(drawer);
						}, 100);
					});
			} else {
				const config = fetchConfig('javascript');

				config.headers['X-Requested-With'] = 'XMLHttpRequest';

				delete config.headers['Content-Type'];

				const formData = new FormData();

				formData.append('id', this.variantId);
				formData.append('quantity', 1);

				if (cartDrawer) {
					formData.append(
						'sections',
						cartDrawer.getSectionsToRender().map((section) => section.section),
					);
				} else {
					formData.append('sections', getCartBubbleSectionNames().join(','));
				}
				formData.append('sections_url', window.location.pathname);

				config.body = formData;

				fetch(`${window.routes.cart_add_url}.js`, config)
					.then((response) => response.json())
					.then((parsedState) => {
						if (cartDrawer) {
							cartDrawer.renderContents(parsedState);
						} else {
							updateCartBubbles(parsedState);
						}

						const cartUpdatedEvent = new CustomEvent('cart:updated', {
							detail: parsedState,
						});

						const imagesReloadEvent = new CustomEvent('lazy-image:reload', {
							bubbles: true,
							detail: this,
						});

						const cartDrawerLoadEvent = new CustomEvent('cart-drawer:load', {
							bubbles: true,
							detail: this,
						});

						document.dispatchEvent(imagesReloadEvent);
						document.dispatchEvent(cartUpdatedEvent);
						document.dispatchEvent(cartDrawerLoadEvent);
					})
					.catch((error) => {
						this.classList.add(classes.hasError);
					})
					.finally(() => {
						button.classList.remove(classToAdd);
						button.removeAttribute('disabled');
					});
			}
		}

		preventVariantURLSwitching() {
			const variantPicker = this.productElement?.querySelector('variant-radios,variant-selects');

			if (!variantPicker) return;

			variantPicker.setAttribute('data-update-url', 'false');
		}
	}

	if (!customElements.get('quick-buy')) {
		customElements.define('quick-buy', QuickBuy);
	}

	class ColorSwatches extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				parent: '.product-item',
				items: '.color-swatches__list-item',
				quickBuy: 'quick-buy',
				firstImage: '.product-media__first-image',
				secondImage: '.product-media__second-image',
				videoWrapper: '.product-media__video',
				priceWrapper: '.product-item__price',
				labelsWrapper: '.product-item__labels',
			};

			this.classes = {
				colorSwatch: 'color-swatches__list-item',
				colorSwatchSelected: 'color-swatches__list-item--selected',
			};

			this.action = this.dataset.action || 'quick-buy';
			this.loadId = 0;
			this.defaultVideoAspectRatio = null;

			// Elements
			this.parent = this.closest(this.selectors.parent);
			this.items = this.querySelectorAll(this.selectors.items);
			this.defaultVariantId = this.querySelector(`.${this.classes.colorSwatchSelected}`)?.dataset.id;
			this.quickBuy =
				this.parent.querySelector(this.selectors.quickBuy) ||
				this.parent.parentElement?.querySelector(this.selectors.quickBuy);

			this.init();
		}

		init() {
			this.addEventListeners();
		}

		addEventListeners() {
			const { classes } = this;

			this.addEventListener('click', (e) => {
				e.preventDefault();

				const target = e.target.closest(`.${classes.colorSwatch}`);

				if (!target || target.classList.contains(classes.colorSwatchSelected)) return;

				this.updateProductItemVariant(target);
			});

			if (this.action === 'change-details') {
				this.addEventListener('pointerenter', (e) => {
					const target = e.target.closest(`.${classes.colorSwatch}`);

					if (!target || !target.dataset.mediaSrc) return;

					const baseUrl = target.dataset.mediaSrc.replace(/[?&]width=\d+/, '');
					const preload = new Image();
					preload.src = this.buildImageUrl(baseUrl, 1024);
				}, true);
			}
		}

		updateProductItemVariant(target) {
			const { quickBuy } = this;
			const variantID = target.dataset.id;

			if (quickBuy) {
				quickBuy.setAttribute('data-id', variantID);
			}

			this.updateClasses(target);

			if (this.action === 'change-details') {
				this.updateProductImage(target);
				this.updateProductPrice(target);
				this.updateProductLabels(target);
			} else if (quickBuy) {
				quickBuy.click();
			}
		}

		buildImageUrl(baseUrl, width) {
			const separator = baseUrl.includes('?') ? '&' : '?';

			return `${baseUrl}${separator}width=${width}`;
		}

		updateProductImage(target) {
			const mediaSrc = target.dataset.mediaSrc;

			if (!mediaSrc) return;

			const firstImage = this.parent.querySelector(this.selectors.firstImage);
			const videoWrapper = this.parent.querySelector(this.selectors.videoWrapper);
			const mediaContainer = this.parent.querySelector('.product-media__images');

			if (!firstImage && !videoWrapper) return;

			const isDefault = target.dataset.id === this.defaultVariantId;
			const baseUrl = mediaSrc.replace(/[?&]width=\d+/, '');
			const mediaFocal = target.dataset.mediaFocal;

			if (!isDefault && mediaContainer) {
				mediaContainer.classList.add('is-loading');
			}

			this.loadId++;
			const currentLoadId = this.loadId;

			const onImageReady = () => {
				if (currentLoadId !== this.loadId) return;

				if (mediaContainer) {
					mediaContainer.classList.remove('is-loading');
				}
			};

			if (videoWrapper && !firstImage) {
				const mediaWidth = target.dataset.mediaWidth;
				const mediaHeight = target.dataset.mediaHeight;
				this.updateVideoOverlay(videoWrapper, isDefault, baseUrl, mediaFocal, mediaWidth, mediaHeight, onImageReady);

				if (isDefault) {
					onImageReady();
				}
			}

			if (firstImage) {
				this.updateFirstImage(firstImage, target, baseUrl, mediaFocal, onImageReady);
			}

			const secondImage = this.parent.querySelector(this.selectors.secondImage);

			if (secondImage) {
				secondImage.style.display = isDefault ? '' : 'none';
			}

			const imagesReloadEvent = new CustomEvent('lazy-image:reload', {
				bubbles: true,
				detail: this,
			});

			document.dispatchEvent(imagesReloadEvent);
		}

		updateFirstImage(firstImage, target, baseUrl, mediaFocal, onImageReady) {
			const picture = firstImage.querySelector('picture');
			const sources = firstImage.querySelectorAll('source');
			const img = firstImage.querySelector('img');

			if (!img) {
				onImageReady();
				return;
			}

			const mediaWidth = target.dataset.mediaWidth;
			const mediaHeight = target.dataset.mediaHeight;

			sources.forEach((source) => {
				const srcset = source.getAttribute('srcset');

				if (!srcset) return;

				const newSrcset = srcset
					.split(',')
					.map((entry) => {
						const parts = entry.trim().split(/\s+/);
						const url = parts[0];
						const descriptor = parts[1] || '';
						const widthMatch = url.match(/[?&]width=(\d+)/);
						const width = widthMatch ? widthMatch[1] : '480';

						return `${this.buildImageUrl(baseUrl, width)} ${descriptor}`.trim();
					})
					.join(', ');

				source.setAttribute('srcset', newSrcset);
			});

			const imgWidthMatch = img.src.match(/[?&]width=(\d+)/);
			const imgWidth = imgWidthMatch ? imgWidthMatch[1] : '1920';

			img.src = this.buildImageUrl(baseUrl, imgWidth);

			if (img.complete) {
				onImageReady();
			} else {
				img.addEventListener('load', onImageReady, { once: true });
				img.addEventListener('error', onImageReady, { once: true });
			}

			if (mediaWidth && mediaHeight) {
				img.width = mediaWidth;
				img.height = mediaHeight;

				if (picture && picture.classList.contains('original')) {
					picture.style.aspectRatio = `${mediaWidth}/${mediaHeight}`;
				}
			}

			if (mediaFocal) {
				img.style.objectPosition = mediaFocal;
			} else {
				img.style.removeProperty('object-position');
			}

			const lazyImageContent = firstImage.querySelector('.lazy-image__content');

			if (lazyImageContent) {
				lazyImageContent.style.backgroundImage = `url('${this.buildImageUrl(baseUrl, 20)}')`;

				if (mediaFocal) {
					lazyImageContent.style.backgroundPosition = mediaFocal;
				} else {
					lazyImageContent.style.removeProperty('background-position');
				}
			}
		}

		updateVideoOverlay(videoWrapper, isDefault, baseUrl, mediaFocal, mediaWidth, mediaHeight, onImageReady) {
			const overlay = videoWrapper.querySelector('.product-media__swatch-overlay');

			if (isDefault) {
				if (overlay) overlay.remove();

				if (this.defaultVideoAspectRatio) {
					videoWrapper.style.aspectRatio = this.defaultVideoAspectRatio;
				} else {
					videoWrapper.style.removeProperty('aspect-ratio');
				}

				return;
			}

			if (videoWrapper.classList.contains('original') && mediaWidth && mediaHeight) {
				if (!this.defaultVideoAspectRatio) {
					this.defaultVideoAspectRatio = getComputedStyle(videoWrapper).aspectRatio;
				}

				videoWrapper.style.aspectRatio = `${mediaWidth}/${mediaHeight}`;
			}

			const src = this.buildImageUrl(baseUrl, 1024);
			const img = overlay?.querySelector('img') || document.createElement('img');

			img.src = src;

			if (img.complete) {
				onImageReady();
			} else {
				img.addEventListener('load', onImageReady, { once: true });
				img.addEventListener('error', onImageReady, { once: true });
			}

			if (mediaFocal) {
				img.style.objectPosition = mediaFocal;
			} else {
				img.style.removeProperty('object-position');
			}

			if (!overlay) {
				const wrapper = document.createElement('div');
				wrapper.className = 'product-media__swatch-overlay';
				wrapper.appendChild(img);
				videoWrapper.appendChild(wrapper);
			}
		}

		updateFromTemplate(target, templateAttr, wrapperSelector) {
			const template = this.querySelector(`template[${templateAttr}][data-id="${target.dataset.id}"]`);

			if (!template) return;

			const wrapper = this.parent.querySelector(wrapperSelector);

			if (!wrapper) return;

			wrapper.innerHTML = '';
			wrapper.appendChild(template.content.cloneNode(true));
		}

		updateProductPrice(target) {
			this.updateFromTemplate(target, 'data-variant-price', this.selectors.priceWrapper);
		}

		updateProductLabels(target) {
			this.updateFromTemplate(target, 'data-variant-labels', this.selectors.labelsWrapper);
		}

		updateClasses(target) {
			const { items, classes } = this;

			items.forEach((item) => {
				item.classList.remove(classes.colorSwatchSelected);
			});

			target.classList.add(classes.colorSwatchSelected);
		}
	}

	if (!customElements.get('color-swatches')) {
		customElements.define('color-swatches', ColorSwatches);
	}
}
